import { env } from "@acme/env/client";
import type { SupportedLanguage } from "@acme/i18n";
import { Spinner } from "@acme/ui/spinner";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
// import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Suspense } from "react";
import { useLanguage } from "~/common/hooks/use-language.js";
import { useTheme } from "~/common/hooks/use-theme.js";
import { FirebaseMessagingProvider } from "~/common/lib/firebase/firebase-messaging.provider.js";
import type { api, queryClient } from "~/common/utils/trpc.js";
import type { AuthContext } from "~/features/auth/_providers/auth.provider.js";

const RootLayout = () => {
  useTheme({ defaultTheme: "system" });
  const { locale: _locale, auth } = Route.useRouteContext();
  const isLoggedIn = auth?.isAuthenticated ?? false;

  // Initialize language from localStorage
  useLanguage();

  if (env.PUBLIC_WEBAPP_IS_IN_MAINTENANCE_MODE) {
    return <div>Maintenance Mode</div>; //TODO: add cool animated screen here!
  }

  return (
    <FirebaseMessagingProvider isLoggedIn={isLoggedIn}>
      <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-background">
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
        {/* <TanStackRouterDevtools /> */}
      </div>
    </FirebaseMessagingProvider>
  );
};

function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Spinner className="size-8 text-muted-foreground" />
    </div>
  );
}

interface RouterContext {
  api: typeof api;
  auth: AuthContext | undefined;
  queryClient: typeof queryClient;
  locale: SupportedLanguage;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  beforeLoad: ({ params }) => {
    const locale = (params as any).locale as SupportedLanguage | undefined;
    return {
      locale: locale || "en",
    };
  },
});
